/**
 * AnalysisJobsModal — 분석 작업 목록(문제 분석 v2). **실패한 분석을 다시 태우는 자리**다.
 *
 * 계약: BE-20260829-reply-fe-0828.md §1-2 —
 *   `GET /api/v2/problem-analysis/list?status=&review_status=&page=&per_page=` → `{items[], page, per_page, total}`
 *   항목 키 analysis_id · analysis_uuid · problem_id · revision · status · review_status ·
 *   user_prompt · edited · model · error · applied_at · created_at · updated_at.
 * 회신이 「생성/변형 목록과 같은 결」이라고 해서 **생성 목록(GenerateProblemModal)의 얼굴을 그대로
 * 가져왔다** — 모달 하나, 행 하나에 배지·시각, 오른쪽에 동작. 새 얼굴을 만들지 않는다.
 *
 * 왜 만들었나: 지금까지 실패(500)·진행 중인 분석이 모이는 자리가 없었다. 문제 상세의 [분석] 탭은
 * 그 문제 하나만 보고, 검색의 「분석 필요만」은 **문제**를 세지 **분석 작업**을 세지 않는다.
 * 그래서 실패한 분석을 찾아 다시 태울 길이 없었다.
 * (다른 화면의 컨트롤은 **화면 글자 그대로** 적는다 — ProblemSearchPanel.jsx:513 의 체크박스 라벨이
 *  「분석 필요만」이다. 「분석 필요만 보기」로 쓰면 아래 안내문이 없는 이름을 가리키게 되고,
 *  그것을 읽고 창을 닫은 사람이 그 글자를 찾아 헤맨다.)
 *
 * 모집단 주의 — 이 목록은 **작업**을 센다. 재분석은 덮어쓰지 않고 revision 이 쌓이므로 같은
 * problem_id 가 여러 줄로 온다. 「분석 안 된 문제」를 찾는 자리는 여기가 아니라 검색의
 * `needs_analysis=true` 다(회신 §2-5: `problem-analysis/current` 는 없고 그쪽으로 흡수됐다).
 *
 * ⚠ **상태 필터를 열지 않았다.** 회신이 `status`·`review_status` 를 **키로만** 주고 값 도메인을
 *   주지 않았다. 없는 상태를 고르게 하면 화면이 거짓말을 한다 — 값을 받으면 그때 연다
 *   (analysisApi.js 의 status 주석 참조 — 다음 백엔드 왕복에서 물을 것으로 남겼다).
 * ⚠ 총 건수는 서버가 준 것이 있을 때만 그린다. 없으면 아무 숫자도 쓰지 않는다 —
 *   한 페이지 건수를 총계로 말하면 그 순간 화면이 사실과 달라진다(analysisApi.fetchAnalysisList).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Modal, DesktopPagination } from '../common';
import { useAuth } from '../../context/AuthContext';
import { formatV2Timestamp } from '../../utils/v2DateTime';
import {
  fetchAnalysisList, requestAnalysis, getAnalysisReviewStatusDisplay,
  ANALYSIS_POLL_MS, AUTH_REDIRECT } from './analysisApi';
// 상태 배지는 목록이 이미 쓰는 정본을 그대로 쓴다 — 110(OCR 스테이징 값)이 빠진 쪽이고,
// 모르는 값이 오면 이름을 지어내지 않고 숫자를 그대로 보인다(reply-batch §3-1 정정 반영본).
import { getAnalysisStatusDisplay, ANALYSIS_POLLING_STATUSES } from './problemApi';
import ProblemIdLink from './ProblemIdLink';
import '../../styles/desktop-expanded.css';

const PER_PAGE = 20;

// 목록의 시각 표기 — 생성 목록(GenerateProblemModal)과 같은 한국 시각이다
const shortTime = (v) => formatV2Timestamp(v);

function AnalysisJobsModal({ isOpen, onClose, onOpenProblem = null }) {
  const { getAccessToken } = useAuth();
  const [rows, setRows] = useState({ items: [], page: 1, totalItems: null, totalPages: null });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [retryingIds, setRetryingIds] = useState(() => new Set());

  const pageRef = useRef(1);
  const aliveRef = useRef(true);
  const inFlightRef = useRef(new Set()); // 같은 프레임의 두 번째 클릭은 상태로 못 막는다
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = useCallback(async (page = 1, { silent = false } = {}) => {
    pageRef.current = page;
    if (!silent) setLoading(true);
    try {
      const d = await fetchAnalysisList({ page, perPage: PER_PAGE }, getAccessToken);
      if (!aliveRef.current) return;
      setRows(d);
    } catch (e) {
      if (!aliveRef.current) return;
      // 이 목록이 곧 폴링의 근거다 — 조용히 실패하면 화면은 낡은 채로 남고 사용자는 모른다
      setNotice({ tone: 'error', text: e.message || '분석 작업 목록을 불러오지 못했습니다.' });
    } finally {
      if (aliveRef.current && !silent) setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { if (isOpen) load(1); }, [isOpen, load]);

  // ── 폴링 — 진행 중인 작업이 있을 때만. 200/500 이 되는 순간 조건이 꺼지며 스스로 멈춘다.
  //    종료 조건은 problemApi.ANALYSIS_POLLING_STATUSES(100·150)를 쓴다. analysisApi 주석의
  //    110 표기는 정정 전 값이라(reply-batch §3-1) 그것으로 기다리면 안 오는 상태를 기다린다.
  const polling = isOpen && rows.items.some((it) => ANALYSIS_POLLING_STATUSES.includes(it.status));
  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(() => load(pageRef.current, { silent: true }), ANALYSIS_POLL_MS);
    return () => clearInterval(t);
  }, [polling, load]);

  // ── 다시 분석 — 이 화면의 존재 이유. 새 revision 이 쌓이고 기존 이력은 남는다(계약 §2-2).
  //    여기서는 지시(user_prompt)를 받지 않는다 — 지시가 필요하면 문제 상세의 [분석] 탭에서
  //    창을 열어 쓴다. 그래서 dedup:'already' 때도 「지시가 버려졌다」는 말을 하지 않는다(쓴 적이 없다).
  const retry = async (item) => {
    const pid = item.problem_id;
    if (pid == null || inFlightRef.current.has(pid)) return;
    inFlightRef.current.add(pid);
    setRetryingIds((s) => new Set(s).add(pid));
    setNotice(null);
    try {
      const d = await requestAnalysis(pid, {}, getAccessToken);
      if (d === AUTH_REDIRECT) return; // 로그인으로 튕겼다 — 성공했다고 말하지 않는다
      if (!aliveRef.current) return;
      setNotice({
        tone: 'info',
        text: d?.dedup === 'already'
          ? `#${pid} 이미 분석이 진행 중입니다 — 이어서 기다립니다.`
          : `#${pid} 분석을 다시 요청했습니다.`,
      });
      await load(pageRef.current, { silent: true });
    } catch (e) {
      if (!aliveRef.current) return;
      setNotice({
        tone: 'error',
        text: e.httpStatus === 409
          ? `#${pid} 본문이 확정되지 않은 문제입니다 — 본문 확정 후 분석 가능합니다.`
          : `#${pid} ${e.message || '분석 요청에 실패했습니다.'}`,
      });
    } finally {
      inFlightRef.current.delete(pid);
      if (aliveRef.current) setRetryingIds((s) => { const n = new Set(s); n.delete(pid); return n; });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="분석 작업" size="xl" maxHeight="88vh">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">전체 분석 작업</span>
          {/* 서버가 총 건수를 줄 때만 쓴다 — 없으면 아무 숫자도 쓰지 않는다 */}
          {rows.totalItems != null && (
            <span className="text-[11px] text-gray-400">{rows.totalItems}건</span>
          )}
          <button
            type="button"
            onClick={() => load(pageRef.current)}
            disabled={loading}
            aria-label="분석 작업 목록 새로고침"
            title="새로고침"
            className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {/* 이 목록이 무엇을 세는지 · 왜 필터가 없는지를 화면에서 말한다 — 둘 다 오해가 나던 자리다 */}
        <p className="text-[11px] text-gray-400">
          문제가 아니라 <b className="font-medium text-gray-500">분석 작업</b>을 셉니다 — 다시 분석하면 같은 문제가 판(revision)마다 한 줄씩 쌓입니다.
          개념·난이도가 비어 있는 <b className="font-medium text-gray-500">문제</b>를 찾는 자리는 문제 목록의 검색 조건 「분석 필요만」 체크박스입니다.
        </p>
        <p className="text-[11px] text-gray-400">
          상태로 거르는 기능은 아직 없습니다 — 서버가 쓰는 상태 값 목록을 받지 못했습니다.
        </p>

        <p
          role={notice?.tone === 'error' ? 'alert' : 'status'}
          aria-live={notice?.tone === 'error' ? 'assertive' : 'polite'}
          className={`text-xs ${notice ? (notice.tone === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
        >
          {notice?.text || ''}
        </p>

        {rows.items.length === 0 ? (
          <p className="py-8 text-center text-xs text-gray-400">
            {loading ? '불러오는 중…' : '분석 작업이 없습니다.'}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {rows.items.map((it) => {
              const st = getAnalysisStatusDisplay(it.status);
              const rv = getAnalysisReviewStatusDisplay(it.review_status);
              const busy = retryingIds.has(it.problem_id);
              return (
                <li key={it.analysis_id ?? it.analysis_uuid} className="rounded-xl border border-gray-200 p-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* 번호 — 두 메뉴가 함께 쓰는 정본 부품(ProblemIdLink). 이 목록에는 need_*
                        가 실리지 않아 색은 먹색으로 굳는다 — 모르는 것을 초록으로 말하지 않는다.
                        여는 일은 이 창이 대신한다(onOpen): 모달을 닫고 [분석] 탭을 세운 뒤
                        옮겨야 해서, 링크의 기본 이동만으로는 배경만 조용히 바뀐다. */}
                    <ProblemIdLink
                      id={it.problem_id}
                      linked={!!onOpenProblem}
                      onOpen={onOpenProblem}
                      className="shrink-0"
                    />
                    {it.revision != null && (
                      <span className="shrink-0 text-[10px] text-gray-400">rev {it.revision}</span>
                    )}
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`}>{st.text}</span>
                    {rv && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] ${rv.cls}`}>{rv.text}</span>}
                    {it.edited && (
                      <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700">검수 수정됨</span>
                    )}
                    {it.applied_at && (
                      <span className="shrink-0 text-[10px] text-gray-400" title="문제은행에 반영된 시각">
                        반영 {shortTime(it.applied_at)}
                      </span>
                    )}
                    <span className="ml-auto shrink-0 text-[10px] text-gray-400">
                      {shortTime(it.updated_at || it.created_at)}
                    </span>
                    <button
                      type="button"
                      onClick={() => retry(it)}
                      disabled={busy}
                      aria-label={`${it.problem_id}번 문제 다시 분석`}
                      title="새 판(revision)으로 다시 분석합니다 — 기존 이력은 남습니다"
                      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Sparkles aria-hidden="true" className="size-3" />
                      {busy ? '요청 중…' : '다시 분석'}
                    </button>
                  </div>
                  {/* 실패 사유 — 이 목록의 존재 이유라 자르지 않고 편다 */}
                  {it.error && (
                    <p className="mt-1 whitespace-pre-wrap break-words text-[11px] text-danger">{String(it.error)}</p>
                  )}
                  {/* 이 작업을 무슨 지시로 태웠는지(회신 §1-2 신규 키) — 다시 태울 때의 판단 근거다 */}
                  {it.user_prompt && (
                    <p className="mt-1 break-words text-[11px] text-gray-500">
                      <span className="text-gray-400">지시</span> {String(it.user_prompt)}
                    </p>
                  )}
                  {it.model && (
                    <p className="mt-1 truncate text-[10px] text-gray-400">{String(it.model)}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* 페이지 — 총 페이지 수를 서버에서 알 수 있을 때만 세운다(모르면 안 그린다) */}
        {rows.totalPages > 1 && (
          <div className="pt-1">
            <DesktopPagination
              compact
              currentPage={rows.page}
              totalPages={rows.totalPages}
              onPageChange={(p) => { if (!loading) load(p); }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default AnalysisJobsModal;
